import React, { useCallback, useEffect, useState } from 'react';
import { Container, Grid, Typography, Box, Skeleton } from '@mui/material';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getEntries } from '../services/contentfulClient';
import Seo from '../components/seo/Seo';

const contentfulLocale = (i18nLang) => {
  const normalizedLang = (i18nLang || '').split('-')[0];

  const i18nToContentfulLocale = {
    en: 'en-US',
    pl: 'pl-PL',
  };

  return i18nToContentfulLocale[normalizedLang] || normalizedLang;
};

const normalizeAssetUrl = (url) => {
  if (!url) return '';
  return url.startsWith('//') ? `https:${url}` : url;
};

const projectImageUrl = (entry) => {
  const f = entry?.fields;
  const asset = f?.coverSmall;
  return normalizeAssetUrl(asset?.fields?.file?.url);
};

const projectTitle = (entry) => entry?.fields?.name || entry?.fields?.title || '';

const projectIndustry = (entry) => entry?.fields?.industryShort || entry?.fields?.projectType || '';

const projectDetailPath = (entry) => {
  const slug = entry?.fields?.slug;
  return slug ? `/project/${slug}` : `/project/${entry?.sys?.id}`;
};

const ProjectCoverImage = ({ src, alt }) => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(false);
  }, [src]);

  return (
    <>
      {!loaded ? (
        <Skeleton
          variant="rectangular"
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: 0,
          }}
        />
      ) : null}

      {src ? (
        <Box
          component="img"
          src={src}
          alt={alt}
          loading="lazy"
          decoding="async"
          onLoad={() => setLoaded(true)}
          onError={() => setLoaded(true)}
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center',
            opacity: loaded ? 1 : 0,
            transition: 'opacity 200ms ease',
          }}
        />
      ) : (
        <Skeleton
          variant="rectangular"
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: 0,
          }}
        />
      )}
    </>
  );
};

const ProjectList = () => {
  const { i18n, t } = useTranslation('projects');
  const title = `Betonoki - ${t('selectedProjects', { defaultValue: 'Projects' })}`;
  const description =
    t('metaDescription', {
      defaultValue:
        'Wybrane projekty Betonoki. Zobacz realizacje i informacje o inwestycjach.',
    });
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const locale = contentfulLocale(i18n.language);
      const { items } = await getEntries({
        content_type: 'project',
        limit: 100,
        order: '-sys.createdAt',
        locale,
      });
      setProjects(items);
      // Test-only: duplicate incoming Contentful items 7x
      // setProjects(Array.from({ length: 7 }, () => items).flat());
    } catch (err) {
      console.warn('Contentful projects:', err.message);
    } finally {
      setLoading(false);
    }
  }, [i18n.language]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  return (
    <Container>
      <Seo title={title} description={description} path="/projects" />
      <Typography variant="h1">
        {t('selectedProjects')}
      </Typography>
      {loading ? (
        <Grid container spacing={2} sx={{ marginTop: '2rem' }}>
          {Array.from({ length: 8 }).map((_, idx) => (
            <Grid item xs={6} sm={6} md={3} key={`project-skeleton-${idx}`}>
              <Box sx={{ overflow: 'hidden' }}>
                <Skeleton variant="rectangular" width="100%" height={160} />
                <Skeleton width="80%" height={28} sx={{ mt: 1 }} />
                <Skeleton width="90%" height={22} sx={{ mt: 0.5 }} />
              </Box>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Grid container spacing={2} sx={{ marginTop: '2rem' }}>
          {projects.map((entry) => (
            <Grid item xs={12} sm={6} md={4} key={entry.sys.id}
            sx={{
              width: '100%',
              }}>

              <Link
                to={projectDetailPath(entry)}
                style={{ textDecoration: 'none', color: 'inherit', display: 'block', width: '100%', height: '100%' }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    aspectRatio: '16 / 9',
                    overflow: 'hidden',
                    backgroundColor: 'rgba(0,0,0,0.04)',
                    transform: 'translateZ(0)',
                    '& .project-cover-img': {
                      willChange: 'transform',
                      backfaceVisibility: 'hidden',
                      transform: 'translateZ(0)',
                      transition: 'transform 300ms ease',
                      transformOrigin: 'center',
                    },
                    '&:hover .project-cover-img': {
                      transform: 'translate3d(0,0,0) scale(1.06)',
                    },
                    '&:hover .project-overlay': {
                      opacity: 1,
                    },
                  }}
                >
                  <Box className="project-cover-img" sx={{ position: 'absolute', inset: 0 }}>
                    <ProjectCoverImage src={projectImageUrl(entry)} alt={projectTitle(entry)} />
                  </Box>

                  <Box
                    className="project-overlay"
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center',
                      padding: '1rem',
                      backgroundColor: 'rgba(0,0,0,0.75)',
                      opacity: 0,
                      transition: 'opacity 300ms ease',
                      color: 'white',
                    }}
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography sx={{ fontSize: '1.05rem', fontWeight: 700 }}>
                        {projectTitle(entry)}
                      </Typography>
                      <Box
                        sx={{
                          mt: 0.75,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 4,
                          flexWrap: 'wrap',
                        }}
                      >
                        {entry.fields.projectType ? (
                          <Typography variant="body2">{projectIndustry(entry)}</Typography>
                        ) : null}
                        {entry.fields.location ? (
                          <Typography variant="body2">{entry.fields.location}</Typography>
                        ) : null}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Link>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
};

export default ProjectList;
